<script setup lang="ts">
/**
 * UI-kit component
 * Category: 'Kanban components'
 * Name: 'Tutor Kanban Card'
 * FIGMA: 'https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=14488-36461&t=la4UMlP3KCbig5TQ-4'
 */
import type { TutorKanbanCardProps } from '../types/interfaces'

import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { PaidLabel } from '@shared/model'
import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { IconContainer } from '@shared/ui/other'
import { KanbanDeadline, KanbanPriority } from '@shared/ui/kanban'

import { useTutorKanbanCard } from '../composables/use-tutor-kanban-card.hook'

defineOptions({
  name: 'TutorKanbanCard',
})

const props = withDefaults(defineProps<TutorKanbanCardProps>(), {
  agentName: null,
  expired: false,
  upcoming: false,
  priority: null,
  deadline: null,
  paidLabel: null,
})

const emit = defineEmits<{
  click: [tutorId: number]
}>()

const {
  handleClick,
} = useTutorKanbanCard({
  props,
  onClick: (tutorId: number) => emit('click', tutorId),
})
</script>

<template>
  <div class="tkb-card__wrapper">
    <div
      :class="['tkb-card', { expired, upcoming }]"
      @click="handleClick"
    >
      <div
        class="tkb-card__header"
      >
        <AppTooltip
          text="no assign"
          placement="top"
          size="small"
        >
          <IconContainer
            v-if="paidLabel === PaidLabel.NoAssign"
            class="tkb-card__icon"
          >
            <FontAwesomeIcon :icon="faCircleXmark" />
          </IconContainer>
        </AppTooltip>
        <div class="tkb-card__name">
          <AppTooltip
            :text="name"
            placement="top"
            size="small"
          >
            <AppText name="body2-medium">
              {{ name }}
            </AppText>
          </AppTooltip>
        </div>
        <AppText
          name="body2"
          color="slate-500"
          class="tkb-card__id"
        >
          #{{ tutorId }}
        </AppText>
      </div>
      <div class="tkb-card__info">
        <KanbanPriority
          v-if="priority"
          :status="priority"
          :kanban-priority="kanbanPriority"
        />
        <div class="tkb-card__agent-row">
          <AppText
            name="body2"
            :class="['agent', { 'no-agent': !agentName }]"
          >
            {{ agentName ?? 'Agent unassigned' }}
          </AppText>
          <KanbanDeadline
            v-if="deadline"
            :value="deadline"
            :expired="expired"
            :upcoming="upcoming"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_tutor-kanban-card.scss" />
